import { ORDER_LIFECYCLE_STAGE_LABELS } from "@erp/shared-types";
import type { OrderLifecycleStage } from "@erp/shared-types";
import { parseMoney, roundMoney, sumMoney, toMoneyNumber } from "./money";

/**
 * The sales-orders CSV, shared by the page export (browser) and the chat
 * agent's export tool (server) so the two files never drift. Typed over the
 * structural shape both list DTOs already satisfy.
 */
export type SalesOrdersCsvSubmissionPath =
  "adapter" | "registered_api" | "approve_only";

export interface SalesOrdersCsvSourceRow {
  id: string;
  purchaseOrderNumber: string | null;
  source: string | null;
  splitGroupId: string | null;
  revisionNumber: number;
  lifecycleStage: OrderLifecycleStage;
  rejectionAwaitingRevision: boolean;
  submissionFailed: boolean;
  originId: string | null;
  receivedDate: string | null;
  customerName: string | null;
  totalAmount: string | number | null;
  currency: string | null;
  lineItemCount: number;
  shipToLocationName: string | null;
  salesOrderOriginId: string | null;
  supersededByDraftId?: string | null;
  submissionPath?: SalesOrdersCsvSubmissionPath | null;
}

export interface SalesOrdersCsvLabels {
  rowNumber: string;
  poNumber: string;
  source: string;
  split: string;
  revision: string;
  status: string;
  rejectedAwaitingNewPo: string;
  submissionFailed: string;
  originId: string;
  received: string;
  customer: string;
  amount: string;
  currency: string;
  items: string;
  destination: string;
  erpOrderNumber: string;
  yes: string;
  /** Dedupe columns (appended after the page's columns). */
  splitGroup: string;
  superseded: string;
  submissionPath: string;
  submissionPathValues: Record<SalesOrdersCsvSubmissionPath, string>;
}

/** Plain-English labels for server-generated files (same strings the page translates). */
export const SALES_ORDERS_CSV_DEFAULT_LABELS: SalesOrdersCsvLabels = {
  rowNumber: "#",
  poNumber: "PO Number",
  source: "Source",
  split: "Split",
  revision: "Revision",
  status: "Status",
  rejectedAwaitingNewPo: "Rejected · awaiting new PO",
  submissionFailed: "Submission Failed",
  originId: "Origin ID",
  received: "Received",
  customer: "Customer",
  amount: "Amount",
  currency: "Currency",
  items: "Items",
  destination: "Destination",
  erpOrderNumber: "ERP Order #",
  yes: "Yes",
  splitGroup: "Split Group",
  superseded: "Superseded",
  submissionPath: "Submission Path",
  submissionPathValues: {
    adapter: "ERP adapter",
    registered_api: "Registered API",
    approve_only: "Approved without ERP",
  },
};

export interface SalesOrdersCsvOptions {
  labels: SalesOrdersCsvLabels;
  /** Mirrors the table: the ERP Order # column only exists for Oracle orgs. */
  includeErpOrderNumber: boolean;
  /** Append Split Group / Superseded / Submission Path after the page columns. */
  includeDedupeColumns?: boolean;
  /** Received-date cell formatter; default is the UTC calendar date `YYYY-MM-DD`. */
  formatReceivedDate?: (isoDate: string) => string;
}

function csvAmount(amount: string | number | null): number | string {
  if (amount == null) return "";
  const n = Number(amount);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : amount;
}

export function formatReceivedDateIso(isoDate: string): string {
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime())
    ? isoDate
    : parsed.toISOString().slice(0, 10);
}

/** Ordered header list, so a writer can pin column order explicitly. */
export function salesOrdersCsvColumns({
  labels,
  includeErpOrderNumber,
  includeDedupeColumns,
}: Pick<
  SalesOrdersCsvOptions,
  "labels" | "includeErpOrderNumber" | "includeDedupeColumns"
>): string[] {
  const columns = [
    labels.rowNumber,
    labels.poNumber,
    labels.source,
    labels.split,
    labels.revision,
    labels.status,
    labels.submissionFailed,
    labels.originId,
    labels.received,
    labels.customer,
    labels.amount,
    labels.currency,
    labels.items,
    labels.destination,
  ];
  if (includeErpOrderNumber) columns.push(labels.erpOrderNumber);
  if (includeDedupeColumns) {
    columns.push(labels.splitGroup, labels.superseded, labels.submissionPath);
  }
  return columns;
}

/**
 * Flattens one list row into a CSV record, one column per piece of information
 * the table shows (badges included). Amount stays numeric so spreadsheets can
 * sum it.
 */
export function toSalesOrdersCsvRow(
  row: SalesOrdersCsvSourceRow,
  index: number,
  {
    labels,
    includeErpOrderNumber,
    includeDedupeColumns = false,
    formatReceivedDate = formatReceivedDateIso,
  }: SalesOrdersCsvOptions,
): Record<string, unknown> {
  const record: Record<string, unknown> = {
    [labels.rowNumber]: index + 1,
    [labels.poNumber]: row.purchaseOrderNumber ?? "",
    [labels.source]: row.source ?? "",
    [labels.split]: row.splitGroupId ? labels.yes : "",
    [labels.revision]: row.revisionNumber,
    [labels.status]:
      row.lifecycleStage === "rejected" && row.rejectionAwaitingRevision
        ? labels.rejectedAwaitingNewPo
        : ORDER_LIFECYCLE_STAGE_LABELS[row.lifecycleStage],
    [labels.submissionFailed]: row.submissionFailed ? labels.yes : "",
    [labels.originId]: row.originId ?? "",
    [labels.received]: row.receivedDate
      ? formatReceivedDate(row.receivedDate)
      : "",
    [labels.customer]: row.customerName ?? "",
    [labels.amount]: csvAmount(row.totalAmount),
    [labels.currency]: row.currency ?? "",
    [labels.items]: row.lineItemCount,
    [labels.destination]: row.shipToLocationName ?? "",
  };
  if (includeErpOrderNumber) {
    record[labels.erpOrderNumber] = row.salesOrderOriginId ?? "";
  }
  if (includeDedupeColumns) {
    record[labels.splitGroup] = row.splitGroupId
      ? row.splitGroupId.slice(0, 8)
      : "";
    record[labels.superseded] = row.supersededByDraftId ? labels.yes : "";
    record[labels.submissionPath] = row.submissionPath
      ? labels.submissionPathValues[row.submissionPath]
      : "";
  }
  return record;
}

// ---------------------------------------------------------------------------
// Summary — the analyst's numbers, computed from the same rows the file holds.
// ---------------------------------------------------------------------------

export const NO_CURRENCY_KEY = "(none)";

export interface SalesOrdersCurrencyTotals {
  currency: string;
  orderCount: number;
  /** Distinct customer POs: split siblings collapse onto their split group. */
  poCount: number;
  lineCount: number;
  totalAmount: number;
  /** Rows counted in `orderCount` whose amount is blank (excluded from `totalAmount`). */
  blankAmountCount: number;
}

/** Every row summed as entered, regardless of currency — the spreadsheet SUM(Amount). */
export interface SalesOrdersGrandTotal {
  orderCount: number;
  poCount: number;
  lineCount: number;
  totalAmount: number;
  blankAmountCount: number;
  /** Rows whose currency is blank (they are inside `totalAmount`). */
  blankCurrencyCount: number;
}

export interface SalesOrdersRowsSummary {
  /** Every exported row. */
  all: SalesOrdersCurrencyTotals[];
  /** Reached the ERP (adapter or registered-API path). */
  submittedToErp: SalesOrdersCurrencyTotals[];
  /** Subset of `submittedToErp` that already shows an ERP order number. */
  submittedWithErpNumber: SalesOrdersCurrencyTotals[];
  /** `submittedToErp` minus `submittedWithErpNumber`. */
  erpNumberPending: SalesOrdersCurrencyTotals[];
  approvedWithoutErpCount: number;
  supersededCount: number;
  rowCount: number;
  /** Cross-currency totals for each population above; quote with `blankCurrencyCount`. */
  grandTotals: {
    all: SalesOrdersGrandTotal;
    submittedToErp: SalesOrdersGrandTotal;
    submittedWithErpNumber: SalesOrdersGrandTotal;
    erpNumberPending: SalesOrdersGrandTotal;
  };
}

/** Two-decimal sum of the rows' amounts; blanks are counted, not summed. */
function sumAmounts(rows: SalesOrdersCsvSourceRow[]): {
  totalAmount: number;
  blankAmountCount: number;
} {
  const amounts = rows
    .map((r) => r.totalAmount)
    .filter((a): a is string | number => a != null && a !== "");
  return {
    totalAmount: toMoneyNumber(
      roundMoney(sumMoney(amounts.map((a) => parseMoney(a))), 2),
    ),
    blankAmountCount: rows.length - amounts.length,
  };
}

function totalsByCurrency(
  rows: SalesOrdersCsvSourceRow[],
): SalesOrdersCurrencyTotals[] {
  const groups = new Map<string, SalesOrdersCsvSourceRow[]>();
  for (const row of rows) {
    const key = row.currency?.trim() || NO_CURRENCY_KEY;
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, group]) => {
      return {
        currency,
        orderCount: group.length,
        poCount: new Set(group.map((r) => r.splitGroupId ?? r.id)).size,
        lineCount: group.reduce((n, r) => n + (r.lineItemCount ?? 0), 0),
        ...sumAmounts(group),
      };
    });
}

function grandTotal(rows: SalesOrdersCsvSourceRow[]): SalesOrdersGrandTotal {
  return {
    orderCount: rows.length,
    poCount: new Set(rows.map((r) => r.splitGroupId ?? r.id)).size,
    lineCount: rows.reduce((n, r) => n + (r.lineItemCount ?? 0), 0),
    ...sumAmounts(rows),
    blankCurrencyCount: rows.filter((r) => !r.currency?.trim()).length,
  };
}

function reachedErp(row: SalesOrdersCsvSourceRow): boolean {
  return (
    row.submissionPath === "adapter" || row.submissionPath === "registered_api"
  );
}

function hasErpNumber(row: SalesOrdersCsvSourceRow): boolean {
  return (
    typeof row.salesOrderOriginId === "string" &&
    row.salesOrderOriginId.trim() !== ""
  );
}

/** The analyst's block: totals per population, per currency, from the rows themselves. */
export function summarizeSalesOrderRows(
  rows: SalesOrdersCsvSourceRow[],
): SalesOrdersRowsSummary {
  const submittedToErp = rows.filter(reachedErp);
  const withNumber = submittedToErp.filter(hasErpNumber);
  const pending = submittedToErp.filter((r) => !hasErpNumber(r));
  return {
    all: totalsByCurrency(rows),
    submittedToErp: totalsByCurrency(submittedToErp),
    submittedWithErpNumber: totalsByCurrency(withNumber),
    erpNumberPending: totalsByCurrency(pending),
    approvedWithoutErpCount: rows.filter(
      (r) => r.submissionPath === "approve_only",
    ).length,
    supersededCount: rows.filter((r) => !!r.supersededByDraftId).length,
    rowCount: rows.length,
    grandTotals: {
      all: grandTotal(rows),
      submittedToErp: grandTotal(submittedToErp),
      submittedWithErpNumber: grandTotal(withNumber),
      erpNumberPending: grandTotal(pending),
    },
  };
}
